import { useState, useEffect } from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { useTranslation, useSession, Toggle, ToggleStyle } from '@nextgen-web-framework/all';
import { ConfigService, type AppState } from '@nextgen-web-framework/core';
import { useDispatch, useSelector } from 'react-redux';
import { getBonusData } from '@commerce-webcomponents/sdk';
import BonusPerformance from '../BonusPerformance/BonusPerformance';
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';
import styles from './BonusPerformanceWithToggle.module.scss';
import { BonusPerformanceWithToggleProps } from './types/propsTypes';

const BonusPerformanceWithToggle = (props: BonusPerformanceWithToggleProps) => {
  const dispatch = useDispatch();
  const { session } = useSession();
  const t = useTranslation();
  const configs = ConfigService.getConfigs();
  const { bonusData } = useSelector((state: AppState) => state.amshop);
  const [enableBonusPerformance, setEnableBonusPerformance] = useState<boolean>(false);
  const today = new Date();
  const currentMonth = today.getMonth() + 1 < 10 ? `0${today.getMonth() + 1}` : `${today.getMonth() + 1}`;
  const currentYearMonth = `${today.getFullYear()}${currentMonth}`;

  useEffect(() => {
    if (enableBonusPerformance) {
      dispatch(getBonusData({ period: currentYearMonth, aboNumber: session.aboNum }) as unknown as AnyAction);
    }
  }, [enableBonusPerformance]);

  const renderBonusPerformanceLoader = () => (
    <div className={styles['bonus-performance-loader']}>
      <div className={styles['bonus-performance-loader-upper']}>
        <SkeletonLoader height={50} borderRadius={8} />
        <SkeletonLoader height={64} borderRadius={8} />
        <SkeletonLoader height={21} borderRadius={8} />
      </div>
      <div className={styles['bonus-performance-loader-bottom']}>
        <div className={styles['bonus-performance-loader-box']}>
          <span className={styles['bonus-performance-loader-box-title']} />
          <SkeletonLoader height={28} borderRadius={8} />
        </div>
        <div className={styles['bonus-performance-loader-box']}>
          <span className={styles['bonus-performance-loader-box-title']} />
          <SkeletonLoader height={28} borderRadius={8} />
        </div>
      </div>
    </div>
  );

  const renderBonusPerformance = () => (
    <>
      {bonusData ? (
        <BonusPerformance
          {...bonusData}
          maxBonusPercent={props.maxBonusPercent ?? Number(configs.NEXT_BONUS_HIGHEST_LEVEL)}
        />
      ) : (
        renderBonusPerformanceLoader()
      )}
    </>
  );

  return (
    <div
      className={styles['bonus-performance-toggle-container']}
      data-testid="bonus-performance-toggle-container-testid"
    >
      <div className={styles['bonus-performance-toggle-section']}>
        <div className={styles['bonus-performance-toggle-wrapper']}>
          <div className={styles['bonus-performance-toggle-title']}>
            <span className={styles['bonus-performance-toggle-title-text']}>
              {t('web.bonusPerformance.toggle.title')}
            </span>
          </div>
          <div className={styles['bonus-performance-toggle-switch']}>
            <Toggle
              toggleStyle={ToggleStyle.SWITCH}
              checked={enableBonusPerformance}
              onClick={() => setEnableBonusPerformance(!enableBonusPerformance)}
              data-testid="bonus-performance-toggle-testid"
            />
          </div>
        </div>
      </div>
      {enableBonusPerformance && renderBonusPerformance()}
    </div>
  );
};

export default BonusPerformanceWithToggle;
